/**
 * DocumentCard - Displays a document in the grid view with action buttons.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Edit3, FolderInput, Paperclip, Trash2, Clock } from 'lucide-react';
import { Card, CardHeader, CardContent, CardFooter, Badge, IconContainer, CardAction } from '@/components/ui';
import type { SavedDocument } from '@/lib/types';
import { getCategoryIcon, getCategoryColor, formatDate, getContentPreview, getCategoryIconVariant, stripMarkdown } from '../utils';

interface DocumentCardProps {
  document: SavedDocument;
  isSelected: boolean;
  isDeleting: boolean;
  onView: (doc: SavedDocument) => void;
  onEdit: (doc: SavedDocument) => void;
  onMove: (doc: SavedDocument) => void;
  onSources: (doc: SavedDocument) => void;
  onDelete: (docId: string) => void;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document: doc,
  isSelected,
  isDeleting,
  onView,
  onEdit,
  onMove,
  onSources,
  onDelete,
}) => {
  const { t } = useTranslation();
  const colors = getCategoryColor(doc.category);
  const Icon = getCategoryIcon(doc.category);
  const iconVariant = getCategoryIconVariant(doc.category);
  const hasSources = !!doc.transcriptionId || !!doc.attachments?.photoCount || !!doc.attachments?.noteCount;

  return (
    <Card
      variant={isSelected ? 'selected' : 'interactive'}
      borderColor={isSelected ? colors.border : undefined}
      className="group relative"
    >
      {/* Header */}
      <CardHeader className="mb-3">
        <div className="flex items-center gap-2">
          <IconContainer variant={iconVariant} size="sm">
            <Icon className="w-5 h-5" />
          </IconContainer>
          {doc.projectName && (
            <Badge variant="default" size="sm">
              {doc.projectName}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Title */}
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2 text-sm sm:text-base">
          {stripMarkdown(doc.title)}
        </h3>

        {/* Preview */}
        <p className="text-xs sm:text-sm text-slate-500 mb-3 line-clamp-2">
          {getContentPreview(doc.content)}
        </p>
      </CardContent>

      {/* Footer */}
      <CardFooter className="text-[11px] sm:text-xs">
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(doc.createdAt)}
        </span>
        {doc.templateType && (
          <Badge variant="default" size="sm">
            {doc.templateType}
          </Badge>
        )}
      </CardFooter>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 px-4 pb-2">
        <div className="flex items-center gap-1">
          <CardAction color="blue" iconOnly onClick={() => onView(doc)} title={t('projects.viewDocument')}>
            <Eye className="w-4 h-4" />
          </CardAction>
          <CardAction color="indigo" iconOnly onClick={() => onEdit(doc)} title={t('projects.editDocument')}>
            <Edit3 className="w-4 h-4" />
          </CardAction>
          <CardAction color="default" iconOnly onClick={() => onMove(doc)} title={t('projects.moveDocument')}>
            <FolderInput className="w-4 h-4" />
          </CardAction>
          {hasSources && (
            <CardAction color="default" iconOnly onClick={() => onSources(doc)} title={t('projects.viewSources')}>
              <Paperclip className="w-4 h-4" />
            </CardAction>
          )}
        </div>
        <div className="flex items-center gap-1">
          <CardAction color="red" iconOnly onClick={() => onDelete(doc.id)} disabled={isDeleting} title={t('projects.deleteDocument')}>
            <Trash2 className="w-4 h-4" />
          </CardAction>
        </div>
      </div>
    </Card>
  );
};

export default DocumentCard;
